import React, { useEffect, useContext, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useSignUpFlow } from "components/context/signup/SignUpFlowContext"
import { useHistory } from "react-router"
import CarrotClient from "lib/carrot-api"
import Settings from "utils/CarrotConfig"
import { isHttpError } from "utils/HttpErrors"
import { CarrotErrorCodes } from "utils/CarrotErrors"

interface SignUpFindAccountSuccessProviderProps {
  onSubmit: () => void
  error: string
  loading: boolean
}

const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

const SignUpFindAccountSuccessContext = React.createContext<SignUpFindAccountSuccessProviderProps>(null)

export const useSignUpFindAccountSuccess = () => useContext(SignUpFindAccountSuccessContext)

export function SignUpFindAccountSuccessProvider({ children }) {
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const formMethods = useForm({ reValidateMode: "onSubmit" })
  const history = useHistory()
  const { getHasFoundAccount, firstName, lastName, dateOfBirth } = useSignUpFlow()

  useEffect(() => {
    if (!getHasFoundAccount()) {
      history.push("/signup")
    }
  }, [])

  const onSubmit = formMethods.handleSubmit(async ({ email }) => {
    setError("")
    setLoading(true)
    try {
      await client.signUpAddEmailToAccount(email, firstName, lastName, dateOfBirth)
    } catch (error) {
      if (isHttpError(error)) {
        const { code } = await error.response.json()
        if (code === CarrotErrorCodes.EMPLOYEE_ALREADY_HAS_EMAIL) {
          setError("Something went wrong. Try again later.")
          setLoading(false)
          return
        }
      }
      setError("Something went wrong. Try again later.")
      setLoading(false)
      return
    }
    try {
      await client.signUp(email)
    } catch (error) {
      setError("Something went wrong. Try again later.")
      setLoading(false)
      return
    }
    history.push(`/signup-success?email=${encodeURIComponent(email)}`)
  })

  return (
    <FormProvider {...formMethods}>
      <SignUpFindAccountSuccessContext.Provider value={{ error, loading, onSubmit }}>
        {children}
      </SignUpFindAccountSuccessContext.Provider>
    </FormProvider>
  )
}
